/*

  settings to account for

  - [X] auto
  - [X] program (application)
  - [x] analog inputs
    - [x] mode
    - [x] test 1
    - [x] test 2
  - [x] digital inputs
    - [x] mode
  - [x] com source
  - [X] schedule

*/

import React, { useState, useEffect, useCallback } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import PropTypes from 'prop-types'

import { SelectInput, DecimalInput, isNumber } from '../Inputs/Inputs'
import AppDispatcher from '../../../dispatchers/AppDispatcher'

import styles from '../../../../css/settings.module.css'
import RedX from '../Graphics/RedX'

const DEFAULT_PHYSICAL = {
  pulseMultiplier: 0.01,
  pulseUnit: 'gal',
  flowLow: 0,
  flowUse: 'none',
}

const FEEDBACK_INPUT_USE_VALID = [
  { name: 'No Feedback Use', value: 'none' },
  { name: 'Feedback for Relay 1', value: 'machine1Feedback' },
  { name: 'Feedback for Relay 2', value: 'machine2Feedback' },
]

function Pump (props) {
  const {
    device: {
      physical = DEFAULT_PHYSICAL,
    } = {},
    setValidity,
    intl: {
      formatMessage,
    },
  } = props

  const {
    pulseMultiplier: defaultPulseMultiplier = DEFAULT_PHYSICAL.pulseMultiplier,
    pulseUnit: defaultPulseUnit = DEFAULT_PHYSICAL.pulseUnit,
    flowLow: defaultFlowLow = DEFAULT_PHYSICAL.flowLow,
    flowUse: defaultFlowUse = DEFAULT_PHYSICAL.flowUse,
  } = physical

  const [formIsValid, setFormIsValid] = useState(true)
  const [invalidFields, setInvalidFields] = useState({})
  const [pulseMultiplier, setPulseMultiplier] = useState(defaultPulseMultiplier)
  const [pulseMultiplierHolder, setPulseMultiplierHolder] = useState(1 / defaultPulseMultiplier)
  const [pulseUnit, setPulseUnit] = useState(defaultPulseUnit)
  const [flowLow, setFlowLow] = useState(defaultFlowLow)
  const [flowUse, setFlowUse] = useState(defaultFlowUse)

  const appDispatch = useCallback(() => {
    const physicals = {
      pulseMultiplier: (1 / pulseMultiplierHolder),
      pulseUnit,
      flowLow,
      flowUse,
    }

    AppDispatcher.dispatch({
      action: 'change-physical',
      setting: physicals,
    })
  }, [pulseMultiplierHolder, pulseUnit, flowLow, flowUse])

  useEffect(() => {
    appDispatch()
  }, [pulseMultiplierHolder, pulseUnit, flowLow, flowUse])

  const childChange = useCallback((event) => {
    const { target: { name, value, validity: { valid } } } = event
    let newValue = value

    switch (name) {
      case 'flowUse':
        setFlowUse(value)
        break
      case 'pulseUnit':
        setPulseUnit(value)
        break
      case 'pulseMultiplier':
        setPulseMultiplier(Number(value))
        break
      case 'flowLow':
        setFlowLow(Number(value))
        break
      case 'pulseMultiplierHolder':
        newValue = Math.round(Number(value * 1000)) / 1000
        setPulseMultiplierHolder(newValue)
        break
      default:
        return
    }

    const newInvalidFields = { ...invalidFields }

    if (valid) {
      delete newInvalidFields[name]
    } else {
      newInvalidFields[name] = true
    }

    const newFormIsValid = Object.keys(newInvalidFields).length === 0

    setInvalidFields(newInvalidFields)
    setFormIsValid(newFormIsValid)
    setValidity(newFormIsValid)
  }, [invalidFields, setValidity])

  const unitStyle = { opacity: 0.7, marginBottom: '10px' }
  const redX = (
    <RedX
      style={{
        position: 'absolute',
        height: '20px',
        right: '4px',
        top: '8px',
        width: '20px',
      }}
    />
  )

  const textInputStyle = {
    height: '32px',
    position: 'relative',
    width: '100%',
  }

  const valueInRange = (
    value,
    min = 0,
    max = Number.MAX_VALUE
  ) => isNumber(value) && ((value < min) || (value > max)) && redX

  return (
    <div>
      <section className={styles.heading}>
        <h3>
          <FormattedMessage
            id='setting.pump.flowMeter.label'
            defaultMessage='Flow Meter Settings'
            description='Flow Meter Settings label'
          />
        </h3>
      </section>
      <div className={styles.inputArea}>
        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.flowMeter.flowPerPulse'
              defaultMessage='Units Per Pulse'
              description='Units Per Pulse label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <div style={textInputStyle}>
              <DecimalInput
                value={Math.round(pulseMultiplierHolder * 10) / 10}
                childChange={childChange}
                field='pulseMultiplierHolder'
                min={0}
                style={styles.controlNumberInput}
              />
            </div>
          </div>
        </section>
        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.flowMeter.flowUnit'
              defaultMessage='Flow Units'
              description='Flow Units label'
            />
          </h5>
          <div className={styles.controlSelect}>
            <SelectInput
              value={pulseUnit}
              options={[
                { name: formatMessage({ id: 'units.gallon.label', defaultMessage: 'Gallon' }), value: 'gal' },
                { name: formatMessage({ id: 'units.acreFoot.label', defaultMessage: 'Acre Foot' }), value: 'ac-ft' },
                { name: formatMessage({ id: 'units.liter.label', defaultMessage: 'Liter' }), value: 'L' },
                { name: formatMessage({ id: 'units.cubicMeter.label', defaultMessage: 'Cubic Meter' }), value: 'm3' },
              ]}
              childChange={childChange}
              field='pulseUnit'
              style={styles.controlSelectInput}
            />
          </div>
        </section>
        <section>
          <h5>
            Use
          </h5>
          <div className={styles.controlSelect}>
            <SelectInput
              value={flowUse}
              options={FEEDBACK_INPUT_USE_VALID}
              childChange={childChange}
              field='flowUse'
              style={styles.controlSelectInput}
            />
          </div>
        </section>
        <section>
          <h5>
            <FormattedMessage
              id='setting.flow.alert.value.label'
              defaultMessage='Flow below'
              description='flow alert value label'
            />
          </h5>
          <div className={styles.controlNumber} style={{ marginBottom: '0px' }}>
            <div style={textInputStyle}>
              <DecimalInput
                value={flowLow || DEFAULT_PHYSICAL.flowLow}
                childChange={childChange}
                field='flowLow'
                max={10000}
                min={0}
                style={styles.controlNumberInput}
              />
              {valueInRange(flowLow, 0, 10000)}
            </div>
          </div>
          <div style={unitStyle}>
            {
              {
                'ac-ft': formatMessage({ id: 'units.acreFoot.minute', defaultMessage: 'ac-ft/m' }),
                gal: formatMessage({ id: 'units.gallon.minute', defaultMessage: 'gpm' }),
                m3: formatMessage({ id: 'units.cubicMeter.minute', defaultMessage: 'm3/m' }),
                L: formatMessage({ id: 'units.liter.minute', defaultMessage: 'L/m' }),
              }[pulseUnit || DEFAULT_PHYSICAL.pulseUnit]
            }
          </div>
        </section>
      </div>
    </div>
  )
}

Pump.propTypes = {
  device: PropTypes.object.isRequired,
  setValidity: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
}

export default injectIntl(Pump)
